/**
 * Kaparós sorsjegy – canvas maszk (`destination-out`).
 *
 * A canvas DÍSZ: ha nincs, ha hibázik, ha a látogató billentyűzetet használ,
 * a feliratkozás akkor is működik (`forceReveal`). Soha nem lehet oka annak,
 * hogy valaki ne tudjon feliratkozni.
 *
 * A nyeremény NEM ismert ebben a fájlban – a küszöb átlépésekor a hívó indítja
 * a szerveroldali sorsolást. A DOM-ban a kaparás előtt nincs nyeremény-szöveg.
 */

/** Kaparás-ellenőrzés ritkítása: minden 16. pixel, és max 10 vizsgálat/mp.
 *  A `getImageData` drága – fojtás nélkül a húzás akadozna gyenge telefonon. */
const SAMPLE_STRIDE = 4 * 16;
const CHECK_INTERVAL_MS = 100;

export function initScratchCard(canvas, options = {}) {
  const {
    onThreshold,
    threshold = 0.4,
    coverColor = '#39B54A',        // brand zöld (tailwind.config: brand)
    label = 'KAPARJ LE',
  } = options;

  if (!canvas || typeof canvas.getContext !== 'function') return null;

  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = rect.width || canvas.clientWidth || 300;
  const h = rect.height || canvas.clientHeight || 160;
  canvas.width = Math.max(1, Math.round(w * dpr));
  canvas.height = Math.max(1, Math.round(h * dpr));

  // `willReadFrequently`: enélkül a Chrome minden getImageData-nál GPU→CPU
  // másolást csinál, ami húzás közben látható akadást okoz.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.scale(dpr, dpr);
  ctx.fillStyle = coverColor;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(255,255,255,.72)';
  ctx.font = 'bold 22px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, w / 2, h / 2);

  ctx.globalCompositeOperation = 'destination-out';
  ctx.lineWidth = 40;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  let drawing = false;
  let fired = false;
  let lastCheck = 0;
  let destroyed = false;

  const pos = (e) => {
    const r = canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const clearedRatio = () => {
    try {
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let clear = 0;
      let total = 0;
      for (let i = 3; i < d.length; i += SAMPLE_STRIDE) {
        total += 1;
        if (d[i] === 0) clear += 1;
      }
      return total ? clear / total : 0;
    } catch {
      // Néhány böngésző „tainted canvas”-t jelez, ha a lapon kereszt-origós
      // rajzolás történt. Nem hagyhatjuk beragadni a látogatót: felfedjük.
      return 1;
    }
  };

  const fire = () => {
    if (fired || destroyed) return;
    fired = true;
    if (typeof onThreshold === 'function') onThreshold();
  };

  const scratch = (e) => {
    if (!drawing || fired || destroyed) return;
    e.preventDefault();
    const [x, y] = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (now - lastCheck > CHECK_INTERVAL_MS) {
      lastCheck = now;
      if (clearedRatio() > threshold) fire();
    }
  };

  const onDown = (e) => {
    if (fired || destroyed) return;
    drawing = true;
    ctx.beginPath();
    const [x, y] = pos(e);
    ctx.moveTo(x, y);
    // Egy pont már önmagában karcoljon – enélkül a koppintás nem hagy nyomot.
    ctx.lineTo(x + 0.01, y);
    ctx.stroke();
    try { canvas.setPointerCapture(e.pointerId); } catch { /* nem kritikus */ }
  };
  const onUp = () => { drawing = false; };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', scratch);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
  canvas.addEventListener('pointerleave', onUp);

  return {
    /** Billentyűzet / fallback / `prefers-reduced-motion` út. */
    forceReveal: () => fire(),
    /** A takaró réteg eltüntetése a felfedés után (vizuális lezárás). */
    clearAll: () => {
      if (destroyed) return;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
    },
    destroy: () => {
      destroyed = true;
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', scratch);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('pointerleave', onUp);
    },
  };
}

/** `prefers-reduced-motion` – ilyenkor a kaparás lépés kimarad, „Felfedem” gomb. */
export const prefersReducedMotion = () => {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

// ── Kliensoldali ismétlés-védelem ────────────────────────────────────────────
// A szerver a valódi kapu (e-mailenként egy élő kupon); ez csak azért van, hogy
// a már nyert látogatónak ne dobjuk fel újra a jegyet, hanem a kódját mutassuk.
const STORAGE_KEY = 'np-vip-scratch';

export const readStoredDraw = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || !v.drawn_at) return null;
    // A tárolt bejegyzés a kupon lejáratáig érvényes; utána újra kaparhat.
    if (v.expires_at && new Date(v.expires_at).getTime() < Date.now()) return null;
    return v;
  } catch {
    return null;
  }
};

export const storeDraw = (entry) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ drawn_at: Date.now(), ...entry }));
  } catch { /* privát mód – a szerveroldali kapu így is véd */ }
};

export const clearStoredDraw = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* nem kritikus */ }
};

// ── Kaparós munkamenet-token (reroll-védelem) ────────────────────────────────
// A /api/vip/scratch első válasza ad egy ALÁÍRT munkamenet-tokent; ezt küldjük
// vissza minden további scratch-hívásban. Amíg a munkamenethez élő jegy
// tartozik, a szerver NEM sorsol újra – refresh, back/forward, másik fül:
// mind ugyanazt a nyereményt kapja. localStorage és nem sessionStorage, mert
// a sessionStorage fülönként külön él, és két fül két sorsolást jelentene.
// A token tartalmáról a kliens semmit nem tud és nem is dönt – a szerver
// állítja ki és a szerver értelmezi (hamisított token = érvénytelen).
const SESSION_KEY = 'np-vip-scratch-session';

export const readScratchSession = () => {
  try { return localStorage.getItem(SESSION_KEY) || ''; } catch { return ''; }
};

export const storeScratchSession = (token) => {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
  } catch { /* privát mód – a szerveroldali kapu így is véd */ }
};
